import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';
import './TopCusines.scss';
import american_cuisine from './img/american_cuisine.png';
import chinese_cuisine from './img/chinese_cuisine.png';
import japanese_cuisine from './img/japanese_cuisine.png';
import mexican_cuisine from './img/mexican_cuisine.png';
import indian_cuisine from './img/indian_cuisine.png';
import columbian_cuisine from './img/columbian_cuisine.png';
import view_all from './img/view_all.png';
import move_right from './img/move_right.png';
import { SectionHeading } from '../Typography';
import { fetchCuisines } from '../../services/api/cuisineService';
import { DINDIN_ASSET_ORIGIN } from '../../services/api/config';

/** Skeleton placeholders while cuisines are loading. */
const SKELETON_COUNT = 8;

/** Pixels tolerance when comparing scroll position to max scroll. */
const SCROLL_EDGE_EPS = 4;

/** One chip (~124px) + gap — used to scroll roughly one item at a time on any breakpoint. */
const CHIP_STRIDE_PX = 136;

/** Slower, eased horizontal scroll (ms) — easier to follow than native smooth scroll. */
const SCROLL_ANIM_MS = 600;

function easeInOutQuad(t) {
  return t < 0.5 ? 2 * t * t : 1 - (-2 * t + 2) ** 2 / 2;
}

/**
 * Animates scrollLeft with easing. Cancels any in-flight animation on this element ref.
 */
function useSmoothHorizontalScroll(scrollRef, updateScrollMeta) {
  const rafRef = useRef(0);

  const cancel = useCallback(() => {
    if (rafRef.current) {
      cancelAnimationFrame(rafRef.current);
      rafRef.current = 0;
    }
  }, []);

  const scrollByDelta = useCallback(
    (delta) => {
      const el = scrollRef.current;
      if (!el || !delta) return;

      cancel();

      const start = el.scrollLeft;
      const maxLeft = el.scrollWidth - el.clientWidth;
      const target = Math.max(0, Math.min(start + delta, maxLeft));
      const change = target - start;
      if (Math.abs(change) < 0.5) {
        updateScrollMeta();
        return;
      }

      const reduceMotion =
        typeof window !== 'undefined' &&
        window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      if (reduceMotion) {
        el.scrollLeft = target;
        updateScrollMeta();
        return;
      }

      const t0 = performance.now();
      const tick = (now) => {
        const t = Math.min(1, (now - t0) / SCROLL_ANIM_MS);
        el.scrollLeft = start + change * easeInOutQuad(t);
        if (t < 1) {
          rafRef.current = requestAnimationFrame(tick);
        } else {
          rafRef.current = 0;
          updateScrollMeta();
        }
      };
      rafRef.current = requestAnimationFrame(tick);
    },
    [scrollRef, cancel, updateScrollMeta],
  );

  useEffect(() => () => cancel(), [cancel]);

  return { scrollByDelta, cancelScrollAnimation: cancel };
}

/**
 * Maps API cuisine names to local assets when `image_url` is empty.
 * Keys are normalized (lowercase, no “cuisine” suffix, spaces → single token).
 */
const LOCAL_IMAGE_BY_KEY = {
  american: american_cuisine,
  chinese: chinese_cuisine,
  japanese: japanese_cuisine,
  mexican: mexican_cuisine,
  indian: indian_cuisine,
  colombian: columbian_cuisine,
  columbian: columbian_cuisine,
};

/** Matches existing CSS variables in `src/index.css` where we have branded colors. */
const BACKGROUND_CSS_VAR_BY_KEY = {
  american: 'var(--cuisine-american)',
  indian: 'var(--cuisine-indian)',
  mexican: 'var(--cuisine-mexican)',
  chinese: 'var(--cuisine-chinese)',
  japanese: 'var(--cuisine-japanese)',
  colombian: 'var(--cuisine-columbian)',
  columbian: 'var(--cuisine-columbian)',
};

const FALLBACK_BACKGROUNDS = [
  '#8899AA',
  '#A8B8C8',
  '#C8A090',
  '#90C8A8',
  '#B8A0C8',
  '#9EC5E8',
];

/**
 * Normalizes a cuisine label for lookup maps (e.g. "Philippines Cuisine" → "philippines").
 */
function cuisineKey(name) {
  return name
    .toLowerCase()
    .replace(/\s+cuisine$/i, '')
    .replace(/[^a-z0-9]+/g, '');
}

function resolveImageSrc(cuisine) {
  const url = (cuisine.image_url || '').trim();
  if (url) {
    // API may return absolute URLs or relative paths (e.g. `/storage/...`).
    if (/^https?:\/\//i.test(url)) return url;
    if (url.startsWith('/')) return `${DINDIN_ASSET_ORIGIN}${url}`;
    return `${DINDIN_ASSET_ORIGIN}/${url}`;
  }
  const key = cuisineKey(cuisine.name);
  return LOCAL_IMAGE_BY_KEY[key] || view_all;
}

function resolveBackground(cuisine, index) {
  const key = cuisineKey(cuisine.name);
  return (
    BACKGROUND_CSS_VAR_BY_KEY[key] ||
    FALLBACK_BACKGROUNDS[index % FALLBACK_BACKGROUNDS.length]
  );
}

/** Label text is clamped in CSS so every chip stays the same size. */
function CuisineLabel({ name }) {
  const text = name.trim();
  return (
    <span className="cuisine_name" title={text}>
      {text}
    </span>
  );
}

export default function TopCusines({ onCuisineSelect, selectedCuisineId = null }) {
  const scrollRef = useRef(null);
  const [cuisines, setCuisines] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [scrollMeta, setScrollMeta] = useState({
    hasOverflow: false,
    canScrollLeft: false,
    canScrollRight: false,
  });

  const updateScrollMeta = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    const { scrollLeft, scrollWidth, clientWidth } = el;
    const maxScrollLeft = scrollWidth - clientWidth;
    const hasOverflow = maxScrollLeft > SCROLL_EDGE_EPS;
    const canScrollLeft = hasOverflow && scrollLeft > SCROLL_EDGE_EPS;
    const canScrollRight =
      hasOverflow && scrollLeft < maxScrollLeft - SCROLL_EDGE_EPS;
    setScrollMeta({ hasOverflow, canScrollLeft, canScrollRight });
  }, []);

  const { scrollByDelta } = useSmoothHorizontalScroll(scrollRef, updateScrollMeta);

  /** One chip width + CSS gap so step matches responsive layout. */
  const scrollStepPx = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return CHIP_STRIDE_PX;
    const first = el.querySelector('.cuisine_main_div');
    if (!first) return CHIP_STRIDE_PX;
    const cs = getComputedStyle(el);
    const gap = parseFloat(cs.gap || cs.columnGap || '12') || 12;
    const w = first.getBoundingClientRect().width;
    return Math.max(72, Math.round(w + gap));
  }, []);

  useLayoutEffect(() => {
    updateScrollMeta();
  }, [cuisines, loading, updateScrollMeta]);

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return undefined;
    const ro = new ResizeObserver(() => updateScrollMeta());
    ro.observe(el);
    return () => ro.disconnect();
  }, [updateScrollMeta]);

  const scrollCuisinesLeft = useCallback(() => {
    scrollByDelta(-scrollStepPx());
  }, [scrollByDelta, scrollStepPx]);

  const scrollCuisinesRight = useCallback(() => {
    scrollByDelta(scrollStepPx());
  }, [scrollByDelta, scrollStepPx]);

  useEffect(() => {
    const ac = new AbortController();
    let cancelled = false;

    (async () => {
      try {
        setLoading(true);
        setError(null);
        const { cuisines: list } = await fetchCuisines({ signal: ac.signal });
        if (!cancelled) setCuisines(list);
      } catch (e) {
        if (e.name === 'AbortError') return;
        if (!cancelled) setError(e.message || 'Could not load cuisines');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
      ac.abort();
    };
  }, []);

  return (
    <>
      <div className="container">
        <div className="top_cusines">
          <SectionHeading>TOP CUISINES</SectionHeading>
        </div>

        {error && (
          <p className="text-danger small mb-2" role="alert">
            {error}
          </p>
        )}

        <div className="top_cusines_row pb-4">
          <div
            className={
              'top_cusines_scroll_wrap' +
              (scrollMeta.canScrollLeft ? ' has-fade-left' : '') +
              (scrollMeta.canScrollRight ? ' has-fade-right' : '')
            }
          >
            <div
              ref={scrollRef}
              className="top_cusines_scroll"
              onScroll={updateScrollMeta}
            >
              <button
                type="button"
                className={`cuisine_main_div${!selectedCuisineId ? ' is-selected' : ''}`}
                style={{ background: 'var(--color-accent)' }}
                onClick={() => onCuisineSelect?.(null)}
                aria-pressed={!selectedCuisineId}
              >
                <img src={view_all} alt="" className="cuisine_img" />
                <span className="cuisine_name">
                  View All
                  <br />
                  Cuisine
                </span>
              </button>

              {loading &&
                Array.from({ length: SKELETON_COUNT }).map((_, i) => (
                  <div
                    key={`sk-${i}`}
                    className="cuisine_main_div"
                    style={{ background: 'var(--color-surface-muted)' }}
                    aria-hidden
                  >
                    <div
                      className="cuisine_img"
                      style={{
                        borderRadius: '50%',
                        background: 'var(--color-divider)',
                      }}
                    />
                    <span className="cuisine_name text-muted">…</span>
                  </div>
                ))}

              {!loading &&
                cuisines.map((cuisine, index) => (
                  <button
                    type="button"
                    key={cuisine.id}
                    className={`cuisine_main_div${String(selectedCuisineId) === String(cuisine.id) ? ' is-selected' : ''}`}
                    style={{ background: resolveBackground(cuisine, index) }}
                    onClick={() => onCuisineSelect?.(cuisine)}
                    aria-pressed={String(selectedCuisineId) === String(cuisine.id)}
                  >
                    <img
                      src={resolveImageSrc(cuisine)}
                      alt=""
                      className="cuisine_img"
                    />
                    <CuisineLabel name={cuisine.name} />
                  </button>
                ))}
            </div>

            {scrollMeta.hasOverflow && (
              <>
                <button
                  type="button"
                  className="top_cusines_scroll_btn top_cusines_scroll_btn--prev"
                  onClick={scrollCuisinesLeft}
                  disabled={!scrollMeta.canScrollLeft}
                  aria-label="Scroll cuisines left"
                >
                  <img
                    src={move_right}
                    alt=""
                    className="top_cusines_scroll_btn_icon"
                  />
                </button>
                <button
                  type="button"
                  className="top_cusines_scroll_btn top_cusines_scroll_btn--next"
                  onClick={scrollCuisinesRight}
                  disabled={!scrollMeta.canScrollRight}
                  aria-label="Scroll cuisines right"
                >
                  <img
                    src={move_right}
                    alt=""
                    className="top_cusines_scroll_btn_icon"
                  />
                </button>
              </>
            )}
          </div>
        </div>
      </div>
    </>
  );
}
